import type { GoogleAddressComponent, PlaceDetailsResult } from './googlePlaces';

/** Flat address fields parsed out of a Google place's components. */
export interface ParsedAddress {
  line1: string;
  line2: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
}

/**
 * Map a Google Places `address_components` array onto our flat form
 * fields. Ported verbatim from the web project so behaviour matches
 * (street_number + route → line1, subpremise → line2, locality/postal_town/
 * sublocality → city, admin_area_1/2 short_name → state, country short_name,
 * postal_code → postalCode). Returns null when there's nothing to parse.
 */
export function parseAddressDetails(details: PlaceDetailsResult | null): ParsedAddress | null {
  if (!details) return null;
  const components: GoogleAddressComponent[] = details.address_components ?? [];
  const get = (type: string): GoogleAddressComponent | undefined =>
    components.find((c) => c.types && c.types.includes(type));

  const streetNumber = get('street_number')?.long_name || '';
  const route = get('route')?.long_name || '';
  const subpremise = get('subpremise')?.long_name || '';
  const city =
    get('locality')?.long_name ||
    get('postal_town')?.long_name ||
    get('sublocality')?.long_name ||
    '';
  const state =
    get('administrative_area_level_1')?.short_name ||
    get('administrative_area_level_2')?.short_name ||
    '';
  const country = get('country')?.short_name || '';
  const postalCode = get('postal_code')?.long_name || '';

  const line1 = [streetNumber, route].filter(Boolean).join(' ');

  return { line1, line2: subpremise, city, state, country, postalCode };
}
